import { FC, useRef } from 'react';
import {
  MapEntityEditorViewState,
  setCopyStopIdAction,
  setSelectedMapStopAreaIdAction,
  setSelectedStopIdAction,
  setSelectedTerminalIdAction,
  useAppAction,
} from '../../redux';
import { useNavigateBackSafely } from '../../utils';
import { useWrapInContextNavigation } from '../common/Forms/NavigationBlocker';
import { useRegisterApolloProtocol } from './ApolloProtocol';
import { MapFooter } from './Footer';
import { Map } from './Map';
import { MapHeader } from './MapHeader';
import { MapLoader } from './MapLoader';
import { RouteEditorRef } from './refTypes';
import { ProvideMapUrlStateContext } from './Utils/mapUrlState';
import { useMapViewState } from './Utils/useMapViewState';

const testIds = { mapPage: 'mapPage' };

export const MapPage: FC = () => {
  const mapRef = useRef<RouteEditorRef>(null);
  const navigateBackSafely = useNavigateBackSafely();

  // For the map page we want to block navigation away for all dirty forms that may be open
  const wrapInContextNavigation = useWrapInContextNavigation('BlockForAll');

  useRegisterApolloProtocol();

  const [, setMapViewState] = useMapViewState();
  const setSelectedStopId = useAppAction(setSelectedStopIdAction);
  const setCopyStopId = useAppAction(setCopyStopIdAction);
  const setSelectedStopAreaId = useAppAction(setSelectedMapStopAreaIdAction);
  const setSelectedTerminalId = useAppAction(setSelectedTerminalIdAction);

  const onCloseMap = wrapInContextNavigation(() => {
    // By setting the view states to NONE here on map close, we can prevent an error which
    // happens if the user has stop placement active and closes the map. The error
    // happens when the line from stop to infra link is being removed.
    setMapViewState({
      stops: MapEntityEditorViewState.NONE,
      stopAreas: MapEntityEditorViewState.NONE,
      terminals: MapEntityEditorViewState.NONE,
    });

    // Also reset any active selections on exit
    setSelectedStopId(undefined);
    setCopyStopId(undefined);
    setSelectedStopAreaId(undefined);
    setSelectedTerminalId(undefined);

    navigateBackSafely('/');
  });

  return (
    <ProvideMapUrlStateContext>
      <div
        data-testid={testIds.mapPage}
        className="flex h-screen w-screen flex-col bg-background"
      >
        <MapHeader onClose={onCloseMap} />

        <Map ref={mapRef} />

        <MapFooter
          onDrawRoute={(vehicleMode) =>
            mapRef.current?.onDrawRoute(vehicleMode)
          }
          onEditRoute={() => mapRef.current?.onEditRoute()}
          onStopEditRoute={() => mapRef.current?.onStopEditRoute()}
          onDeleteRoute={() => mapRef.current?.onDeleteRoute()}
          onCancel={() => mapRef.current?.onCancel()}
          onSave={() => mapRef.current?.onSave()}
        />

        <MapLoader />
      </div>
    </ProvideMapUrlStateContext>
  );
};
